import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leaveRulesApi } from '../api/leaveRulesApi';
import { appToast } from '../../../../components/AppToast';

const DEFAULT_FORM_STATE = {
  name: '',
  status: 'Active',
};

export function useLeaveRulesEntry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editId = id;
  const isEdit = Boolean(editId);

  const [form, setForm] = useState(DEFAULT_FORM_STATE);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setIsLoading(true);
      leaveRulesApi
        .getRuleById(editId)
        .then((record) => {
          if (record) {
            setForm({
              name: record.name || '',
              status: record.status || 'Active',
            });
          } else {
            appToast.error('Leave rule not found.');
            navigate('/payroll/leave-rules');
          }
        })
        .catch(() => {
          appToast.error('Failed to load leave rule details.');
          navigate('/payroll/leave-rules');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [editId, isEdit, navigate]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async ({ exitAfter = true } = {}) => {
    if (!form.name.trim()) {
      appToast.error('Rule Name is required.');
      return;
    }

    setIsSaving(true);
    try {
      if (isEdit) {
        await leaveRulesApi.updateRule(editId, form);
        appToast.success('Leave rule updated successfully!');
      } else {
        await leaveRulesApi.createRule(form);
        appToast.success('Leave rule created successfully!');
      }

      if (exitAfter) {
        navigate('/payroll/leave-rules');
      } else if (!isEdit) {
        setForm(DEFAULT_FORM_STATE);
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || 'Failed to save leave rule.';
      appToast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    state: {
      form,
      isEdit,
      editId,
      isLoading,
      isSaving,
    },
    actions: {
      patchForm,
      handleSave,
    },
  };
}
